export type TaskStatus = 'not-started' | 'pending' | 'complete' | 'failed';
export type IdentityTaskStatus = TaskStatus | 'in-progress';

export interface AccountStatusSource {
  defaultProfileId?: number | null;
  reviewProfileId?: number | null;
  textVerified?: boolean | null;
  veriffFlag?: boolean | null;
  veriffStatus?: string | null;
  verified?: boolean | null;
}

export interface AccountStatus {
  hasPage: boolean;
  claimPending: boolean;
  phoneVerification: TaskStatus;
  idVerification: IdentityTaskStatus;
  pageSetup: TaskStatus;
  eligibleToContribute: boolean;
}

export type VerificationActionLabelStyle = 'full' | 'compact';

export function hasProfileId(id: number | null | undefined): boolean {
  return typeof id === 'number' && Number.isSafeInteger(id) && id > 0;
}

export function normalizeIdentityStatus(
  status: string | null | undefined
): string {
  return (status ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function getAccountStatus(
  user: AccountStatusSource | null | undefined
): AccountStatus {
  const hasPage = hasProfileId(user?.defaultProfileId);
  const claimPending = !hasPage && hasProfileId(user?.reviewProfileId);
  const identityStatus = normalizeIdentityStatus(user?.veriffStatus);

  let idVerification: IdentityTaskStatus;
  if (user?.veriffFlag === true) {
    idVerification = 'complete';
  } else if (
    [
      'declined',
      'rejection',
      'abandoned',
      'expired',
      'kyc expired',
      'approved',
    ].includes(identityStatus)
  ) {
    idVerification = 'failed';
  } else if (!identityStatus || identityStatus === 'updated') {
    idVerification = 'not-started';
  } else if (identityStatus === 'in review') {
    idVerification = 'pending';
  } else {
    // A session without a review decision can be replaced if the user left it.
    idVerification = 'in-progress';
  }

  return {
    hasPage,
    claimPending,
    phoneVerification: user?.textVerified === true ? 'complete' : 'not-started',
    idVerification,
    pageSetup: hasPage ? 'complete' : claimPending ? 'pending' : 'not-started',
    eligibleToContribute: user?.verified === true,
  };
}

export function getVerificationActionLabel(
  user: AccountStatusSource | null | undefined,
  style: VerificationActionLabelStyle = 'full'
): string {
  const status = getAccountStatus(user);
  const action =
    status.idVerification === 'pending' ||
    (status.phoneVerification === 'complete' &&
      status.idVerification === 'complete')
      ? 'check'
      : status.idVerification === 'failed'
      ? 'resolve'
      : 'finish';
  const labels = {
    full: {
      check: 'Check verification status',
      resolve: 'Resolve verification',
      finish: 'Finish verification',
    },
    compact: {
      check: 'Check status',
      resolve: 'Resolve',
      finish: 'Finish',
    },
  } as const;

  return labels[style][action];
}
